"use client";

import { useMemo, useState } from "react";

import { KanbanBoard } from "@kit/kanban";
import { Tables } from "@kit/supabase/database";

import { IssueDetailDialog } from "./issue-detail-dialog";
import { IssueStatusBadge } from "./issue-status-badge";
import { IssueTypeBadge } from "./issue-type-badge";
import { IssueVotesBadge } from "./issue-votes-badge";

export function IssuesKanban(props: {
  issues: Tables<"issues">[];
  canComment: boolean;
  userId: string;
}) {
  const columns = useMemo(() => {
    const cols: Array<{
      id: Tables<"issues">["status"];
      name: React.ReactNode;
      nextColumnId: Tables<"issues">["status"] | null;
      tasks: Array<{
        id: string;
        name: string;
        columnId: Tables<"issues">["status"];
        position: string;
        content: React.ReactNode;
      }>;
    }> = [
      {
        id: "under_review",
        name: <IssueStatusBadge status={"under_review"} />,
        nextColumnId: "planned",
        tasks: [],
      },
      {
        id: "planned",
        name: <IssueStatusBadge status={"planned"} />,
        nextColumnId: "in_progress",
        tasks: [],
      },
      {
        id: "in_progress",
        name: <IssueStatusBadge status={"in_progress"} />,
        nextColumnId: "completed",
        tasks: [],
      },
      {
        id: "completed",
        name: <IssueStatusBadge status={"completed"} />,
        nextColumnId: "closed",
        tasks: [],
      },
    ];

    props.issues.forEach((item) => {
      const task = {
        id: item.id,
        name: item.title,
        columnId: item.status,
        content: (
          <div className={"flex justify-between"}>
            <IssueTypeBadge className={"inline-flex"} type={item.type} />
            <IssueVotesBadge votes={item.votes} />
          </div>
        ),
      };

      const col = cols.find((col) => col.id === task.columnId);

      if (col) {
        col.tasks.push({
          ...task,
          position: (col.tasks.length + 1).toString(),
        });
      }
    });

    return cols;
  }, [props.issues]);

  const [issue, setIssue] = useState<Tables<"issues"> | null>(null);

  return (
    <>
      {issue ? (
        <IssueDetailDialog
          onClose={() => setIssue(null)}
          isOpen={!!issue}
          issue={issue}
          canComment={props.canComment}
        />
      ) : null}

      <KanbanBoard
        onTaskClick={(value) => {
          const issue = props.issues.find((issue) => issue.id === value.id);

          if (issue) {
            setIssue(issue);
          }
        }}
        isReadOnly
        columns={columns}
      />
    </>
  );
}
